/**
 * Paint-line buffers (W2-01a preview-gl; rendering.md R§3.7, ARCHITECTURE.md §7.3 step 3, DESIGN D24).
 *
 * Phira keeps one persistent viewport-sized RGBA buffer per paint line: every frame with paint(t) > 0 stamps a
 * filled circle (an ellipse under the line scale) at the line origin, a frame with paint(t) <= 0 clears it, and the
 * buffer is composited over the frame right after the line. The engine simulates the stamps on a fixed 60 Hz grid
 * from the last clear (`Engine.paintStamps`); off the grid, the stamp of the current time is appended.
 *
 * Here the grid stamps are accumulated into a GPU buffer incrementally (only the stamps added since the last frame
 * are drawn); the buffer is rebuilt from scratch when the clear time changed, time went backwards, the stamps that
 * were drawn changed (an edit), or the viewport size changed. The off-grid stamp of the current time is drawn on
 * top of the composited buffer (identical to drawing it into the buffer last). Stamps are written PREMULTIPLIED
 * (the buffer is a layer) and the layer is composited with ONE / ONE_MINUS_SRC_ALPHA, which equals Phira's
 * straight-alpha result over the frame and keeps the frame's alpha at 1.
 */

import type { Engine, PaintStampBatch } from '../../core/engine/types';
import { FRAME_DT } from '../../core/engine/notes/constants';
import type { LineId } from '../../core/model/types';
import type { Mat2D } from '../../core/util/types';
import { clipFromPx, mulMat, type GlState } from '../gl/glState';
import { colorByte } from '../gl/quadBatch';
import { ColorTarget } from '../gl/renderTargets';
import { viewportPxInt, worldToPx, type PassContext } from './passContext';

type Stamp = PaintStampBatch['stamps'][number];

/** Whether a stamp time lies on the engine's 60 Hz simulation grid. */
export function isGridTime(t: number): boolean {
  const k = Math.floor(t / FRAME_DT + 1e-9);
  return Math.abs(t - k * FRAME_DT) <= 1e-9;
}

/** Number of leading grid stamps (every stamp but an off-grid trailing one). */
export function gridStampCount(stamps: readonly Stamp[]): number {
  const n = stamps.length;
  const last = stamps[n - 1];
  return last !== undefined && !isGridTime(last.t) ? n - 1 : n;
}

function sameStamp(a: Stamp | undefined, b: Stamp | null): boolean {
  if (a === undefined || b === null) return a === undefined && b === null;
  return (
    Object.is(a.t, b.t) &&
    Object.is(a.x, b.x) &&
    Object.is(a.y, b.y) &&
    Object.is(a.rx, b.rx) &&
    Object.is(a.ry, b.ry) &&
    Object.is(a.color[0], b.color[0]) &&
    Object.is(a.color[1], b.color[1]) &&
    Object.is(a.color[2], b.color[2]) &&
    Object.is(a.color[3], b.color[3])
  );
}

interface Buffer {
  readonly target: ColorTarget;
  clearedAt: number;
  drawn: number;
  first: Stamp | null;
  last: Stamp | null;
  lastT: number;
  vpW: number;
}

/** What a composite did (tests and diagnostics). */
export interface PaintCompositeInfo {
  readonly rebuilt: boolean;
  readonly drawnNow: number;
  readonly offGrid: number;
}

export class PaintBuffers {
  private readonly gl: WebGL2RenderingContext;
  private readonly state: GlState;
  private readonly buffers = new Map<LineId, Buffer>();
  last: PaintCompositeInfo = { rebuilt: false, drawnNow: 0, offGrid: 0 };

  constructor(gl: WebGL2RenderingContext, state: GlState) {
    this.gl = gl;
    this.state = state;
  }

  private stampsInto(ctx: PassContext, xf: Mat2D, stamps: readonly Stamp[], from: number, to: number): void {
    const b = ctx.batch;
    b.setProgram(ctx.programs.disc);
    b.setBlend('premultiplied');
    b.setTransform(xf);
    b.setTexture(ctx.textures.white);
    const I: Mat2D = [1, 0, 0, 1, 0, 0];
    for (let i = from; i < to; i++) {
      const s = stamps[i];
      if (s === undefined) continue;
      const c = s.color;
      const a = colorByte(c[3]);
      if (a === 0 || !(s.rx > 0) || !(s.ry > 0)) continue;
      b.rect(
        I,
        s.x - s.rx,
        s.y - s.ry,
        s.x + s.rx,
        s.y + s.ry,
        -1,
        -1,
        1,
        1,
        colorByte(c[0]),
        colorByte(c[1]),
        colorByte(c[2]),
        a,
      );
    }
    b.flush();
  }

  /** Brings the line's buffer up to date and composites it over the bound frame target. */
  composite(ctx: PassContext, line: LineId, engine: Engine): void {
    const list = ctx.list;
    const vp = ctx.vp;
    const batch = engine.paintStamps(line, list.t, vp.W);
    const r = viewportPxInt(ctx);
    if (r.w <= 0 || r.h <= 0) return;
    let buf = this.buffers.get(line);
    if (buf === undefined) {
      buf = {
        target: new ColorTarget(this.gl),
        clearedAt: NaN,
        drawn: 0,
        first: null,
        last: null,
        lastT: NaN,
        vpW: NaN,
      };
      this.buffers.set(line, buf);
    }
    const stamps = batch.stamps;
    const grid = gridStampCount(stamps);
    const resized = buf.target.ensure(r.w, r.h);
    const valid =
      !resized &&
      Object.is(buf.clearedAt, batch.clearedAt) &&
      Object.is(buf.vpW, vp.W) &&
      !(list.t < buf.lastT) &&
      grid >= buf.drawn &&
      (buf.drawn === 0 || (sameStamp(stamps[0], buf.first) && sameStamp(stamps[buf.drawn - 1], buf.last)));
    const st = this.state;
    const frameTarget = ctx.targetFbo;
    // World -> buffer px (viewport origin at 0) -> buffer clip.
    const toBuf = mulMat(clipFromPx(r.w, r.h), mulMat([ctx.kx, 0, 0, ctx.ky, -r.x, -r.y], worldToPx(vp)));
    const from = valid ? buf.drawn : 0;
    // ColorTarget.ensure binds framebuffers behind the state cache's back.
    if (resized) st.forgetTarget();
    if (!valid || from < grid) {
      ctx.batch.flush();
      st.bindTarget(buf.target.fbo, r.w, r.h);
      if (!valid) {
        st.clear(0, 0, 0, 0);
        buf.drawn = 0;
      }
      this.stampsInto(ctx, toBuf, stamps, from, grid);
    }
    st.bindTarget(frameTarget, ctx.targetW, ctx.targetH);
    buf.drawn = grid;
    buf.clearedAt = batch.clearedAt;
    buf.vpW = vp.W;
    buf.lastT = list.t;
    buf.first = stamps[0] ?? null;
    buf.last = grid > 0 ? (stamps[grid - 1] ?? null) : null;
    // Composite the layer over the viewport (image top = texture's last row).
    const b = ctx.batch;
    b.setProgram(ctx.programs.quad);
    b.setBlend('premultiplied');
    b.setTransform(ctx.pxToClip);
    b.setTexture(buf.target.tex);
    b.rect([1, 0, 0, 1, 0, 0], vp.vx, vp.vy, vp.vx + vp.W, vp.vy + vp.H, 0, 1, 1, 0, 255, 255, 255, 255);
    b.flush();
    // The current frame's own stamp when t is between grid frames.
    if (grid < stamps.length) this.stampsInto(ctx, ctx.worldToClip, stamps, grid, stamps.length);
    this.last = { rebuilt: !valid, drawnNow: grid - from, offGrid: stamps.length - grid };
  }

  /** Drops the buffers of paint lines that were not drawn this frame. */
  retain(live: ReadonlySet<LineId>): void {
    for (const [line, buf] of this.buffers) {
      if (live.has(line)) continue;
      buf.target.dispose();
      this.buffers.delete(line);
    }
  }

  get count(): number {
    return this.buffers.size;
  }

  /** Context lost: the GPU objects are gone; forget them (the next frames rebuild from the stamps). */
  forget(): void {
    this.buffers.clear();
  }

  dispose(): void {
    for (const buf of this.buffers.values()) buf.target.dispose();
    this.buffers.clear();
  }
}
